import { useEffect, useState } from 'react';
import { Link } from '@tanstack/react-router';

import { clientApi } from '@/config/axios';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/lib/auth/AuthContext';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { usePermissions } from '@/features/permissions';
import { InlineReferralReview } from '../InlineReferralReview';
import { DecisionDeck, latestCards, needsDecision } from './DecisionDeck';
import { InboxPreparation } from './InboxPreparation';
import { AssistantConversation } from './AssistantConversation';
import { UploadedDocumentInboxSheet } from '@/features/emr/components/UploadedDocumentInboxSheet';
import { PracticeOperations } from './PracticeOperations';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import type { ActionDetail, Candidate, LabRow, RuntimeRun, SlotOption, SourceOption } from './runtimeContracts';

const human = (value: string) => value.replaceAll('_', ' ');
export function ConnectedWorkbench() {
  const { user, organization } = useAuth();
  const { can } = usePermissions();
  if (!user || !organization || !can('use_assistant') || !can('review_assistant_actions'))
    return <p>Assistant review access is required.</p>;
  return (
    <Workbench
      key={`${organization.organization_id}:${user.id}`}
      reviewerId={user.id}
      org={organization.organization_id}
      manage={can('manage_assistant_policy')}
      manageNotes={can('update_patient') && can('read_patient')}
    />
  );
}

export function Workbench({
  org,
  manage,
  reviewerId,
  manageNotes = false,
}: {
  org: string;
  reviewerId: string;
  manage: boolean;
  manageNotes?: boolean;
}) {
  const base = `/api/v1/organizations/${org}/assistant/workbench`;
  const [runs, setRuns] = useState<RuntimeRun[]>([]);
  const [intakeDocument, setIntakeDocument] = useState<string | null>(null);
  const [intakeJob, setIntakeJob] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [revision, setRevision] = useState(0);
  const [focusedRun, setFocusedRun] = useState<string | null>(null);
  const [tab, setTab] = useState('work');
  const [source, setSource] = useState<{ documentId: string; revisionId?: string; pageNumber?: number } | null>(null);
  useEffect(() => {
    const abort = new AbortController();
    const refresh = () =>
      clientApi
        .get<{ runs: RuntimeRun[] }>(`${base}/briefing`, { signal: abort.signal })
        .then(({ data }) => {
          if (!abort.signal.aborted) {
            setRuns(data.runs);
            setError('');
          }
        })
        .catch(() => {
          if (!abort.signal.aborted) {
            setRuns([]);
            setError('The workbench is unavailable. Check practice settings and your review access.');
          }
        });
    void refresh();
    const timer = window.setInterval(() => {
      if (document.visibilityState === 'visible') void refresh();
    }, 10000);
    return () => {
      abort.abort();
      window.clearInterval(timer);
    };
  }, [base, revision]);
  const refresh = () => setRevision((value) => value + 1);
  useEffect(() => {
    if (intakeJob)
      document
        .querySelector('[aria-label="Selected intake review"]')
        ?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
  }, [intakeJob]);
  const pending = latestCards(runs).filter(needsDecision).length;
  return (
    <section aria-label="Assistant workbench" className="flex w-full min-w-0 flex-col gap-4 p-4">
      {!error && (
        <AssistantConversation
          runId={focusedRun}
          onOpenSource={setSource}
          base={base}
          onChanged={refresh}
          onReview={() => setTab('work')}
        />
      )}
      <div role="tablist" aria-label="Assistant views" className="flex flex-wrap gap-2">
        {[
          ['work', `Decisions (${pending})`],
          ['prepare', 'Prepare work'],
          ...(manage ? [['settings', 'Practice settings']] : []),
        ].map(([id, label]) => (
          <Button
            key={id}
            role="tab"
            type="button"
            aria-selected={tab === id}
            variant={tab === id ? 'default' : 'outline'}
            onClick={() => setTab(id)}>
            {label}
          </Button>
        ))}
      </div>
      {error && (
        <p role="alert" className="text-destructive">
          {error}
        </p>
      )}
      {tab === 'settings' && (
        <>
          <Settings base={base} reviewerId={reviewerId} onSaved={refresh} />
          <PracticeOperations base={base} reviewerId={reviewerId} />
        </>
      )}
      {tab === 'prepare' && (
        <>
          <InboxPreparation
            key={base}
            showConversation={false}
            base={base}
            onChanged={refresh}
            onOpenSource={setSource}
            onReviewIntake={(job, documentId) => {
              setIntakeJob(job);
              setIntakeDocument(documentId);
            }}
          />
          {intakeJob && (
            <section aria-label="Selected intake review">
              <InlineReferralReview
                key={intakeJob}
                org={org}
                jobId={intakeJob}
                onOpenCurrentSource={intakeDocument ? () => setSource({ documentId: intakeDocument }) : undefined}
                onBack={() => {
                  setIntakeJob(null);
                  refresh();
                }}
              />
            </section>
          )}
          <PrepareWork
            base={base}
            onPrepared={() => {
              refresh();
              setTab('work');
            }}
          />
        </>
      )}
      {tab === 'work' && (
        <DecisionDeck
          onFocusedRun={setFocusedRun}
          runs={runs}
          base={base}
          org={org}
          manageNotes={manageNotes}
          onRefresh={refresh}
          onSource={setSource}
          renderReview={(id) => (
            <Review
              key={`${id}:${revision}:${runs.flatMap((run) => run.actions).find((action) => action.id === id)?.status}`}
              base={base}
              manage={manage}
              identifier={id}
              onChanged={refresh}
            />
          )}
        />
      )}
      {source && (
        <UploadedDocumentInboxSheet
          open
          documentId={source.documentId}
          revisionId={source.revisionId}
          pageNumber={source.pageNumber}
          onOpenChange={(open) => {
            if (!open) setSource(null);
          }}
        />
      )}
    </section>
  );
}

function LabReviewRow({
  row,
  value,
  reason,
  onValue,
  selected,
  rejected,
  disabled,
  onDecision,
  reportStatus,
}: {
  row: LabRow;
  reportStatus: string;
  selected: boolean;
  rejected: boolean;
  disabled: boolean;
  value: string;
  reason: string;
  onValue: (value: string, reason: string, reportStatus?: string) => void;
  onDecision: (choice: string) => void;
}) {
  const blocked = ['duplicate', 'missing_date', 'possible_duplicate', 'unreadable'].includes(row.comparison);
  return (
    <section className="flex flex-col gap-3 rounded-lg border p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="font-medium">{row.fields.name}</h3>
        <Badge variant="secondary">{human(row.comparison)}</Badge>
      </div>
      <label>
        Result
        <Input
          value={value}
          disabled={disabled || blocked}
          onChange={(event) => onValue(event.target.value, reason, reportStatus)}
        />
      </label>
      <p>
        {row.fields.unit} · collected {row.fields.collected_date || 'not supplied'} · reported{' '}
        {row.fields.reported_date || 'not supplied'}
      </p>
      {(row.fields.accession || row.fields.specimen || row.fields.method) && (
        <p>
          Accession: {row.fields.accession || 'not supplied'} · Specimen: {row.fields.specimen || 'not supplied'} ·
          Method: {row.fields.method || 'not supplied'}
        </p>
      )}
      {row.comparison === 'possible_duplicate' && (
        <p>
          Same result appears elsewhere. Verify whether this is a separate sample in the laboratory review workflow
          before importing.
        </p>
      )}
      <label>
        Report status
        <select
          value={reportStatus}
          disabled={disabled || blocked}
          className="mt-2 w-full rounded-md border bg-background p-2"
          onChange={(event) => onValue(value, reason, event.target.value)}>
          <option value="">Requires clinician confirmation</option>
          {['preliminary', 'final', 'amended', 'corrected'].map((status) => (
            <option key={status} value={status}>
              {status}
            </option>
          ))}
        </select>
      </label>
      <details>
        <summary className="cursor-pointer">Source evidence</summary>
        {Object.entries(row.evidence).map(([field, quote]) => (
          <p key={field} className="whitespace-pre-wrap break-words py-2">
            {human(field)} · page {quote.page}: “{quote.quote}”
          </p>
        ))}
      </details>
      <label>
        Correction or edit reason
        <Input
          value={reason}
          maxLength={500}
          disabled={disabled || blocked}
          onChange={(event) => onValue(value, event.target.value, reportStatus)}
        />
      </label>
      <div className="flex flex-wrap gap-3">
        <label className="flex items-center gap-3">
          <Checkbox
            checked={selected}
            disabled={disabled || blocked}
            onCheckedChange={(checked) => onDecision(checked ? 'select' : 'none')}
          />
          Import this row
        </label>
        <label className="flex items-center gap-3">
          <Checkbox
            checked={rejected}
            disabled={disabled}
            onCheckedChange={(checked) => onDecision(checked ? 'reject' : 'none')}
          />
          Reject this row
        </label>
      </div>
    </section>
  );
}

function PrepareWork({ base, onPrepared }: { base: string; onPrepared: () => void }) {
  const [sources, setSources] = useState<SourceOption[]>([]);
  const [slots, setSlots] = useState<SlotOption[]>([]);
  const [patients, setPatients] = useState<Candidate[]>([]);
  const [query, setQuery] = useState('');
  const [patient, setPatient] = useState('');
  const [source, setSource] = useState('');
  const [slot, setSlot] = useState('');
  const [workflow, setWorkflow] = useState('fax_preparation');
  const [documentId, setDocumentId] = useState('');
  const [batch, setBatch] = useState<Candidate[]>([]);
  const [documents, setDocuments] = useState<{ document_id: string; document_kind: string; title: string }[]>([]);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    const abort = new AbortController();
    void Promise.all([
      clientApi.get<SourceOption[]>(`${base}/sources`, { signal: abort.signal }).then(({ data }) => setSources(data)),
      clientApi.get<SlotOption[]>(`${base}/slots`, { signal: abort.signal }).then(({ data }) => setSlots(data)),
    ]).catch(() => {
      if (!abort.signal.aborted) setError('Unable to load sources or published slots.');
    });
    return () => abort.abort();
  }, [base]);
  useEffect(() => {
    setPatient('');
    setPatients([]);
    if (query.trim().length < 2) return;
    const abort = new AbortController();
    const timer = window.setTimeout(() => {
      void clientApi
        .get<Candidate[]>(`${base}/patients`, { params: { q: query }, signal: abort.signal })
        .then(({ data }) => {
          if (!abort.signal.aborted) setPatients(data);
        })
        .catch(() => {});
    }, 300);
    return () => {
      abort.abort();
      window.clearTimeout(timer);
    };
  }, [base, query]);
  useEffect(() => {
    setDocumentId('');
    setDocuments([]);
    if (!patient || workflow !== 'document_delivery') return;
    const abort = new AbortController();
    void clientApi
      .get<{ document_id: string; document_kind: string; title: string }[]>(`${base}/patients/${patient}/documents`, {
        signal: abort.signal,
      })
      .then(({ data }) => {
        if (!abort.signal.aborted) setDocuments(data);
      })
      .catch(() => {});
    return () => abort.abort();
  }, [base, patient, workflow]);
  async function prepare() {
    setBusy(true);
    setError('');
    const doc = sources.find((s) => s.id === source);
    try {
      await clientApi.post(`${base}/runs`, {
        workflow,
        request_id: crypto.randomUUID(),
        ...(['fax_preparation', 'lab_staging'].includes(workflow)
          ? { document_id: doc?.id, revision_id: doc?.revision_id, source_hash: doc?.source_hash }
          : {
              ...(workflow === 'reminder_batch'
                ? { selected_patient_ids: batch.map((p) => p.id) }
                : { patient_id: patient }),
              ...(workflow === 'schedule' ? { slot_id: slot } : {}),
              ...(workflow === 'document_delivery'
                ? { document_id: documentId.split(':')[1], document_kind: documentId.split(':')[0] }
                : {}),
            }),
      });
      onPrepared();
    } catch {
      setError(
        'Unable to prepare this task. Check the workflow settings, selected source, patient and contact preferences.',
      );
    } finally {
      setBusy(false);
    }
  }
  return (
    <Card>
      <CardHeader>
        <CardTitle>Prepare a task</CardTitle>
        <CardDescription>Preparation creates a draft for review.</CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        <label>
          Workflow
          <select
            value={workflow}
            className="ml-3 rounded-md border bg-background p-2"
            onChange={(event) => setWorkflow(event.target.value)}>
            {['fax_preparation', 'lab_staging', 'reminder', 'reminder_batch', 'document_delivery', 'schedule'].map(
              (id) => (
                <option key={id} value={id}>
                  {human(id)}
                </option>
              ),
            )}
          </select>
        </label>
        {['fax_preparation', 'lab_staging'].includes(workflow) ? (
          <label>
            Extracted source
            <select
              value={source}
              className="ml-3 max-w-full rounded-md border bg-background p-2"
              onChange={(event) => setSource(event.target.value)}>
              <option value="">Choose document</option>
              {sources.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
          </label>
        ) : (
          <>
            <label>
              Find patient by name or MRN
              <Input value={query} onChange={(event) => setQuery(event.target.value)} />
            </label>
            {workflow === 'reminder_batch' ? (
              <div className="flex flex-col gap-3">
                <p>Select up to 50 patients. Each message will require its own approval.</p>
                {patients.map((p) => (
                  <label key={p.id} className="flex items-center gap-3">
                    <Checkbox
                      checked={batch.some((b) => b.id === p.id)}
                      disabled={batch.length >= 50 && !batch.some((b) => b.id === p.id)}
                      onCheckedChange={(checked) =>
                        setBatch(checked ? [...batch, p] : batch.filter((b) => b.id !== p.id))
                      }
                    />
                    {p.name} · {p.dob} · MRN {p.mrn}
                  </label>
                ))}
                <p>{batch.length} patients selected</p>
                {batch
                  .filter((b) => !patients.some((p) => p.id === b.id))
                  .map((p) => (
                    <Button
                      key={p.id}
                      type="button"
                      variant="outline"
                      onClick={() => setBatch(batch.filter((b) => b.id !== p.id))}>
                      Remove {p.name} · {p.mrn}
                    </Button>
                  ))}
              </div>
            ) : (
              <label>
                Patient
                <select
                  value={patient}
                  className="ml-3 max-w-full rounded-md border bg-background p-2"
                  onChange={(event) => setPatient(event.target.value)}>
                  <option value="">Confirm patient</option>
                  {patients.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name} · {p.dob} · MRN {p.mrn}
                    </option>
                  ))}
                </select>
              </label>
            )}
          </>
        )}
        {workflow === 'schedule' && (
          <label>
            Published appointment slot
            <select
              value={slot}
              className="ml-3 max-w-full rounded-md border bg-background p-2"
              onChange={(event) => setSlot(event.target.value)}>
              <option value="">Choose slot</option>
              {slots.map((s) => (
                <option key={s.id} value={s.id}>
                  {new Date(s.starts_at).toLocaleString()} · {s.visit_type} · {s.location}
                </option>
              ))}
            </select>
          </label>
        )}
        {workflow === 'document_delivery' && (
          <label>
            Document to share
            <select
              value={documentId}
              className="mt-2 w-full rounded-md border bg-background p-2"
              onChange={(event) => setDocumentId(event.target.value)}>
              <option value="">Choose approved document</option>
              {documents.map((doc) => (
                <option
                  key={`${doc.document_kind}:${doc.document_id}`}
                  value={`${doc.document_kind}:${doc.document_id}`}>
                  {doc.title}
                </option>
              ))}
            </select>
          </label>
        )}
        <Button
          type="button"
          onClick={() => void prepare()}
          disabled={
            busy ||
            (['fax_preparation', 'lab_staging'].includes(workflow)
              ? !source
              : workflow === 'reminder_batch'
                ? !batch.length
                : !patient) ||
            (workflow === 'schedule' && !slot) ||
            (workflow === 'document_delivery' && !documentId)
          }>
          Prepare for review
        </Button>
        {error && <p role="alert">{error}</p>}
      </CardContent>
    </Card>
  );
}

function ReceiptReview({
  base,
  onSaved,
  execution,
}: {
  base: string;
  execution: ActionDetail['executions'][number];
  onSaved: () => void;
}) {
  const [providerId, setProviderId] = useState(execution.receipt?.provider_id || '');
  const [status, setStatus] = useState('delivered');
  const [confirmed, setConfirmed] = useState(false);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  async function reconcile() {
    setBusy(true);
    setError('');
    try {
      await clientApi.post(`${base}/executions/${execution.id}/reconcile`, {
        status,
        confirmed,
        provider_id: providerId,
      });
      onSaved();
    } catch {
      setError('Unable to reconcile this receipt. Verify the provider ID, current status and your access.');
    } finally {
      setBusy(false);
    }
  }
  return (
    <details className="mt-3 rounded-md border p-3">
      <summary className="cursor-pointer">Reconcile provider outcome</summary>
      <div className="mt-3 flex flex-col gap-3">
        <p className="text-sm">
          Check provider logs using execution {execution.id}. This records a confirmed outcome and never resends the
          message.
        </p>
        <label>
          Provider message ID
          <Input
            value={providerId}
            onChange={(event) => {
              setProviderId(event.target.value);
              setConfirmed(false);
            }}
          />
        </label>
        <label>
          Confirmed outcome
          <select
            value={status}
            className="mt-2 w-full rounded-md border bg-background p-2"
            onChange={(event) => {
              setStatus(event.target.value);
              setConfirmed(false);
            }}>
            <option value="delivered">Delivered</option>
            <option value="accepted">Accepted by provider; delivery pending</option>
            <option value="bounced">Bounced</option>
            <option value="failed">Confirmed failed; no delivery</option>
          </select>
        </label>
        <label className="flex items-center gap-3">
          <Checkbox checked={confirmed} onCheckedChange={(value) => setConfirmed(value === true)} />I checked the
          provider receipt for this execution
        </label>
        <Button type="button" disabled={!confirmed || !providerId || busy} onClick={() => void reconcile()}>
          Record confirmed outcome
        </Button>
        {error && <p role="alert">{error}</p>}
      </div>
    </details>
  );
}

function Review({
  base,
  manage,
  onChanged,
  identifier,
}: {
  base: string;
  identifier: string;
  manage: boolean;
  onChanged: (id: string) => void;
}) {
  const [detail, setDetail] = useState<ActionDetail | null>(null);
  const [patient, setPatient] = useState('');
  const [selected, setSelected] = useState<string[]>([]);
  const [rejected, setRejected] = useState<string[]>([]);
  const [edits, setEdits] = useState<
    Record<string, { value: string; correction_reason: string; report_status?: string }>
  >({});
  const [message, setMessage] = useState('');
  const [dirty, setDirty] = useState(false);
  const [reviewed, setReviewed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  useEffect(() => {
    const abort = new AbortController();
    clientApi
      .get<ActionDetail>(`${base}/actions/${identifier}`, { signal: abort.signal })
      .then(({ data }) => {
        if (abort.signal.aborted) return;
        setDetail(data);
        setPatient(data.patient_id || '');
        setSelected(data.payload.selected_rows || []);
        setRejected(data.payload.rejected_rows || []);
        setMessage(data.payload.message || '');
      })
      .catch(() => {
        if (!abort.signal.aborted) setError('This action is unavailable. Reload the queue.');
      });
    return () => abort.abort();
  }, [base, identifier]);
  if (!detail) return <p role="status">{error || 'Loading prepared action…'}</p>;
  const change = () => {
    setDirty(true);
    setReviewed(false);
  };
  const editable = detail.status === 'pending_review' && !detail.stale;
  async function save() {
    if (!detail) return;
    setBusy(true);
    setError('');
    try {
      const body = {
        expected_hash: detail.hash,
        expected_version: detail.version,
        ...(detail.kind === 'lab_import'
          ? {
              selected_rows: selected,
              rejected_rows: rejected,
              patient_id: patient || null,
              edits: Object.entries(edits).map(([id, edit]) => ({ id, ...edit })),
            }
          : { message }),
      };
      const { data } = await clientApi.post<{ id: string }>(`${base}/actions/${identifier}/revisions`, body);
      onChanged(data.id);
    } catch {
      setError(
        'The draft could not be saved. Check the patient, selected rows and correction reasons, then reload if the source changed.',
      );
    } finally {
      setBusy(false);
    }
  }
  async function decide(decision: 'approve' | 'reject') {
    if (!detail) return;
    setBusy(true);
    setError('');
    try {
      await clientApi.post(`${base}/actions/${identifier}/decisions`, {
        decision,
        reviewed,
        expected_hash: detail.hash,
        request_id: crypto.randomUUID(),
        expected_version: detail.version,
      });
      onChanged(identifier);
    } catch {
      setReviewed(false);
      setError(
        'The decision could not be completed. Check the source, permissions and current draft before trying again.',
      );
    } finally {
      setBusy(false);
    }
  }
  return (
    <Card>
      <CardHeader>
        <CardTitle>{human(detail.kind)}</CardTitle>
        <CardDescription>
          Version {detail.version} · {human(detail.status)}
        </CardDescription>
      </CardHeader>
      <CardContent className="flex min-w-0 flex-col gap-5 break-words">
        {detail.payload.truncated && (
          <p role="alert">
            Preparation is partial. Review the full source before approving selected results; additional rows may
            require manual review.
          </p>
        )}
        {detail.stale && (
          <p role="alert" className="text-destructive">
            The source or approval window changed. Prepare a new task before approving.
          </p>
        )}
        {detail.payload.domain_review_required && (
          <p>
            Existing laboratory extraction reused. Review and finalize its results in the source document’s laboratory
            view. This task records source review only.
          </p>
        )}
        {detail.payload.patient_name && <p className="font-medium">{detail.payload.patient_name}</p>}
        {detail.patient_id && (
          <Link to="/patients/$patientId" search={{ assistant: true }} params={{ patientId: detail.patient_id }}>
            Open patient chart
          </Link>
        )}
        {detail.payload.summary?.map((quote, i) => (
          <blockquote key={i} className="border-l-2 border-primary pl-4">
            <p className="whitespace-pre-wrap break-words">{quote.quote}</p>
            <p className="text-sm text-muted-foreground">
              Source page {quote.page} · characters {quote.start}–{quote.end}
            </p>
          </blockquote>
        ))}
        {detail.kind === 'lab_import' && (
          <>
            <label className="flex flex-col gap-2">
              Confirm patient by name, DOB and MRN
              <select
                value={patient}
                disabled={!editable}
                className="rounded-md border bg-background p-2"
                onChange={(event) => {
                  setPatient(event.target.value);
                  change();
                }}>
                <option value="">Select verified patient</option>
                {detail.payload.candidates?.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name} · DOB {p.dob} · MRN {p.mrn}
                  </option>
                ))}
              </select>
            </label>
            {detail.payload.rows?.map((row) => (
              <LabReviewRow
                key={row.id}
                row={row}
                disabled={!editable}
                rejected={rejected.includes(row.id)}
                selected={selected.includes(row.id)}
                value={edits[row.id]?.value ?? row.fields.value}
                reason={edits[row.id]?.correction_reason ?? row.correction_reason}
                reportStatus={edits[row.id]?.report_status ?? row.fields.report_status?.toLowerCase() ?? ''}
                onValue={(value, correction_reason, report_status) => {
                  setEdits({
                    ...edits,
                    [row.id]: { value, correction_reason, report_status: report_status || undefined },
                  });
                  change();
                }}
                onDecision={(choice) => {
                  setSelected(selected.filter((id) => id !== row.id).concat(choice === 'select' ? [row.id] : []));
                  setRejected(rejected.filter((id) => id !== row.id).concat(choice === 'reject' ? [row.id] : []));
                  change();
                }}
              />
            ))}
          </>
        )}
        {detail.payload.recipient && <p>Recipient: {detail.payload.recipient}</p>}
        {detail.payload.title && <p>Document: {detail.payload.title} (approved version pinned)</p>}
        {detail.payload.tasks?.map((task) => (
          <p key={task.id}>
            {task.label} · due {task.due_date}
          </p>
        ))}
        {detail.payload.message !== undefined && (
          <label className="flex flex-col gap-2">
            Patient message
            <Textarea
              value={message}
              maxLength={2000}
              disabled={!editable}
              onChange={(event) => {
                setMessage(event.target.value);
                change();
              }}
            />
          </label>
        )}
        {detail.payload.starts_at && (
          <p>
            {detail.payload.visit_type} · {new Date(detail.payload.starts_at).toLocaleString()}–
            {new Date(detail.payload.ends_at!).toLocaleTimeString()} · {detail.payload.location}
          </p>
        )}
        {detail.kind === 'referral_review' &&
          (detail.payload.job_id ? (
            <InlineReferralReview org={base.split('/organizations/')[1].split('/')[0]} jobId={detail.payload.job_id} />
          ) : (
            <p role="alert">The intake reference is missing. Refresh the queue before continuing.</p>
          ))}
        {editable && detail.kind !== 'referral_review' && (
          <>
            <Button type="button" variant="outline" disabled={!dirty || busy} onClick={() => void save()}>
              Save reviewed draft
            </Button>
            <label className="flex items-start gap-3">
              <Checkbox
                checked={reviewed}
                disabled={dirty || busy}
                onCheckedChange={(value) => setReviewed(value === true)}
              />
              <span>I reviewed the source, patient and exact action shown in this saved version.</span>
            </label>
            <div className="flex flex-wrap gap-3">
              <Button
                type="button"
                onClick={() => void decide('approve')}
                disabled={
                  !reviewed || dirty || busy || (detail.kind === 'lab_import' && (!patient || !selected.length))
                }>
                {detail.kind === 'fax_review' ? 'Accept reviewed source' : 'Accept'}
              </Button>
              <Button type="button" variant="outline" disabled={dirty || busy} onClick={() => void decide('reject')}>
                Reject action
              </Button>
            </div>
          </>
        )}
        {['expired', 'needs_attention'].includes(detail.status) && (
          <Button type="button" disabled={busy} variant="outline" onClick={() => void decide('reject')}>
            Reject action
          </Button>
        )}
        {detail.status === 'approved' && (
          <Button
            type="button"
            disabled={busy}
            variant="outline"
            onClick={() => {
              setBusy(true);
              void clientApi
                .post(`${base}/actions/${identifier}/cancel`, {})
                .then(() => onChanged(identifier))
                .catch(() => setError('Execution has already started or could not be cancelled.'))
                .finally(() => setBusy(false));
            }}>
            Cancel approved action
          </Button>
        )}
        {detail.executions.map((execution) => (
          <div key={execution.id} role="status">
            <p>Execution: {human(execution.status)}</p>
            {execution.reason && <p>{human(execution.reason)}</p>}
            {execution.receipt?.saved !== undefined && (
              <p>Saved {execution.receipt.saved} reviewed laboratory results.</p>
            )}
            {execution.receipt?.status && <p>{human(execution.receipt.status)}</p>}
            {manage && ['accepted', 'delivery_unknown'].includes(execution.status) && (
              <ReceiptReview base={base} execution={execution} onSaved={() => onChanged(identifier)} />
            )}
          </div>
        ))}
        {error && (
          <p role="alert" className="text-destructive">
            {error}
          </p>
        )}
      </CardContent>
    </Card>
  );
}

function Settings({ base, onSaved, reviewerId }: { base: string; reviewerId: string; onSaved: () => void }) {
  const [version, setVersion] = useState<number | null>(null);
  const [enabled, setEnabled] = useState(false);
  const [automatic, setAutomatic] = useState(false);
  const [recordReview, setRecordReview] = useState(false);
  const [automaticReminders, setAutomaticReminders] = useState(false);
  const [labs, setLabs] = useState(false);
  const [communications, setCommunications] = useState(false);
  const [scheduling, setScheduling] = useState(false);
  const [status, setStatus] = useState('');
  const [transport, setTransport] = useState('disabled');
  const [dailyCap, setDailyCap] = useState(20);
  const [retentionDays, setRetentionDays] = useState(30);
  const [interval, setInterval] = useState(15);
  const [policy, setPolicy] = useState<Record<string, unknown>>({});
  useEffect(() => {
    const abort = new AbortController();
    void clientApi
      .get<Record<string, unknown>>(`${base}/policy`, { signal: abort.signal })
      .then(({ data }) => {
        if (abort.signal.aborted) return;
        setPolicy(data);
        setTransport((data.outbound_mode as string) || 'disabled');
        setDailyCap((data.daily_run_cap as number) || 20);
        setRetentionDays((data.retention_days as number) || 30);
        setInterval((data.reconciliation_minutes as number) || 15);
        setVersion(data.version as number);
        setEnabled(!!data.enabled);
        setAutomatic(!!data.automatic);
        setAutomaticReminders(!!data.automatic_reminders);
        setRecordReview(!!data.record_review_enabled);
        setLabs(!!data.lab_import_enabled);
        setCommunications(!!data.communication_enabled);
        setScheduling(!!data.scheduling_enabled);
      })
      .catch(() => {
        if (!abort.signal.aborted) setStatus('Unable to load settings.');
      });
    return () => abort.abort();
  }, [base]);
  async function save() {
    if (!Number.isInteger(dailyCap) || dailyCap < 1 || dailyCap > 1000) {
      setStatus('Daily preparation cap must be a whole number from 1 to 1,000.');
      return;
    }
    setStatus('Saving…');
    try {
      const { version: _version, ...existing } = policy;
      const { data } = await clientApi.put<{ version: number }>(`${base}/policy`, {
        ...existing,
        enabled,
        automatic,
        daily_run_cap: dailyCap,
        record_review_enabled: recordReview,
        lab_import_enabled: labs,
        outbound_mode: transport,
        expected_version: version,
        reviewer_user_id: reviewerId,
        retention_days: retentionDays,
        scheduling_enabled: scheduling,
        reconciliation_minutes: interval,
        communication_enabled: communications,
        automatic_reminders: automaticReminders,
      });
      setVersion(data.version);
      setStatus('Saved. Existing drafts must be prepared again after a policy change.');
      onSaved();
    } catch {
      setStatus('Settings were not saved. Reload and check your access.');
    }
  }
  return (
    <Card>
      <CardHeader>
        <CardTitle>Practice preparation policy</CardTitle>
        <CardDescription>
          Saving designates you as the reviewer. Automatic preparation covers newly extracted documents from the time it
          is enabled.
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        {[
          { value: enabled, set: setEnabled, label: 'Enable assistant preparation' },
          { value: automatic, set: setAutomatic, label: 'Automatically prepare new faxes and laboratory reports' },
          {
            value: automaticReminders,
            set: setAutomaticReminders,
            label: 'Automatically prepare reminders for due open checklist items with verified contact consent',
          },
          {
            value: recordReview,
            set: setRecordReview,
            label: 'Prepare comprehensive medical records for review (history, imaging and referral details)',
          },
          { value: labs, set: setLabs, label: 'Allow approved laboratory imports' },
          { value: communications, set: setCommunications, label: 'Allow reviewed patient communication drafts' },
          { value: scheduling, set: setScheduling, label: 'Allow approved MohairNotes calendar bookings' },
        ].map((item) => (
          <label key={item.label} className="flex items-center gap-3">
            <Checkbox checked={item.value} onCheckedChange={(value) => item.set(value === true)} />
            {item.label}
          </label>
        ))}
        <div className="grid gap-4 md:grid-cols-3">
          <label>
            Daily preparation cap
            <Input
              min={1}
              max={1000}
              type="number"
              value={dailyCap}
              onChange={(event) => setDailyCap(Number(event.target.value))}
            />
          </label>
          <label>
            Preparation scan interval (minutes)
            <Input
              min={1}
              max={1440}
              type="number"
              value={interval}
              onChange={(event) => setInterval(Number(event.target.value))}
            />
          </label>
          <label>
            Draft retention (days)
            <Input
              min={1}
              max={365}
              type="number"
              value={retentionDays}
              onChange={(event) => setRetentionDays(Number(event.target.value))}
            />
          </label>
        </div>
        <label>
          Patient message execution
          <select
            value={transport}
            className="mt-2 w-full rounded-md border bg-background p-2"
            onChange={(event) => setTransport(event.target.value)}>
            <option value="disabled">Disabled</option>
            <option value="local">Capture locally in synthetic testing</option>
            <option value="ses">AWS SES (requires deployment enablement)</option>
          </select>
        </label>
        <p className="text-sm text-muted-foreground">
          Automatic reminders prepare drafts only. Each recipient requires a separate reviewed approval. Delivery
          outcomes with uncertain status require reconciliation before another attempt.
        </p>
        <Button type="button" disabled={version === null} onClick={() => void save()}>
          Save practice policy
        </Button>
        <p role="status">{status}</p>
      </CardContent>
    </Card>
  );
}
